import React, { useState, useEffect, useRef, lazy, Suspense, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import ProfileEditModal from './components/admin/ProfileEditModal';
import GlobalAnalyticsTab from './components/admin/GlobalAnalyticsTab';
import ChatBot from './components/ChatBot';
import ProfilePreview from './components/ProfilePreview';
import { generateColorPalette, hasGoodContrast, getReadableTextColor } from './utils/colorUtils';

// Error Boundary to prevent white screens
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
            <h2 className="text-xl font-bold text-red-600 mb-4">Algo salió mal</h2>
            <p className="text-gray-600 mb-4">Ha ocurrido un error inesperado. Por favor, recarga la página.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Recargar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Extracted shared components
import PageLoader from './components/shared/PageLoader';
import Skeleton, { SKELETON_CSS } from './components/shared/Skeleton';
import ContactLogo from './components/shared/ContactLogo';
import Icon from './components/shared/Icon';
import { API } from './constants/api';
import { COLOR_THEMES } from './constants/themes';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlanProvider } from './contexts/PlanContext';
import { usePlan } from './contexts/PlanContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import UpgradeModal from './components/shared/UpgradeModal';
import ProOnboardingTour from './components/shared/ProOnboardingTour';
import ProfileSelectionModal from './components/shared/ProfileSelectionModal';
import LockedFeature from './components/shared/LockedFeature';
import ComingSoonOverlay from './components/shared/ComingSoonOverlay';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterOnboardingPage from './pages/RegisterOnboardingPage';
import TeamLoginPage from './pages/TeamLoginPage';
import GoogleOAuthCallback from './pages/GoogleOAuthCallback';
import Homepage from './pages/Homepage';
import LandingPage from './pages/LandingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PublicResetPasswordPage from './pages/PublicResetPasswordPage';
import ComprarPage from './pages/ComprarPage';
import DocsPage from './pages/DocsPage';
import LegalPage from './pages/LegalPage';
import CotizarPage from './pages/CotizarPage';
import AgendaDemo from './pages/AgendaDemo';
import AppPage from './pages/AppPage';
import SobreNosotros from './pages/SobreNosotros';
import WaitlistPage from './pages/WaitlistPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import PlansPage from './pages/PlansPage';
import PaymentFailurePage from './pages/PaymentFailurePage';
import PaymentPendingPage from './pages/PaymentPendingPage';
import ActivationPage from './pages/ActivationPage';
import NotFoundPage from './pages/NotFoundPage';

// Dashboard components
import TeamsPanel from './components/dashboard/TeamsPanel';
import TeamAnalyticsPanel from './components/dashboard/TeamAnalyticsPanel';
import LeadsPanel from './components/dashboard/LeadsPanel';
import HubPanel from './components/dashboard/HubPanel';
import CampaignsPanel from './components/dashboard/CampaignsPanel';
import FollowUpsPanel from './components/dashboard/FollowUpsPanel';
import AnalyticsPanel from './components/dashboard/AnalyticsPanel';
import ProductManagementPanel from './components/dashboard/ProductManagementPanel';
import ProfileEditor from './components/dashboard/ProfileEditor';
import AppearanceEditor from './components/dashboard/AppearanceEditor';
import ProfilesManager from './components/dashboard/ProfilesManager';
import ImageCropModal from './components/dashboard/ImageCropModal';
import SettingsPanel from './components/dashboard/SettingsPanel';
import IntegrationsPanel from './components/dashboard/IntegrationsPanel';
import DeveloperPanel from './components/dashboard/DeveloperPanel';
import BeneficiosPanel from './components/dashboard/BeneficiosPanel';
import PostRegisterPlansModal from './components/dashboard/PostRegisterPlansModal';

// Admin components
import AdminUsersTab from './components/admin/AdminUsersTab';
import AdminRegistrationToggle from './components/admin/AdminRegistrationToggle';
import AdminDisabledSectionsToggle from './components/admin/AdminDisabledSectionsToggle';
import AdminCompaniesTab from './components/admin/AdminCompaniesTab';
import AdminCodesTab from './components/admin/AdminCodesTab';
import AdminProfilesTab from './components/admin/AdminProfilesTab';
import AdminRemindersTab from './components/admin/AdminRemindersTab';
import AdminWaitlistTab from './components/admin/AdminWaitlistTab';
import AdminQuotationsTab from './components/admin/AdminQuotationsTab';
import QuotationConfigTab from './components/admin/QuotationConfigTab';
import LandingEditor from './components/admin/LandingEditor';
import AdminSubscriptionsTab from './components/admin/AdminSubscriptionsTab';
import AdminOrdersTab from './components/admin/AdminOrdersTab';
import AdminPlansTab from './components/admin/AdminPlansTab';
import ClientIntelligenceTab from './components/admin/ClientIntelligenceTab';
import { trackPageView } from './lib/metaPixel';

// Shared components
import Sidebar, { getMenuItems } from './components/shared/Sidebar';
import CommandPalette from './components/shared/CommandPalette';
import ShortcutsModal from './components/shared/ShortcutsModal';

// Lazy load PublicProfile page (large component - 1000+ lines)
const PublicProfile = lazy(() => import('./pages/PublicProfile'));

// Lazy load Card Designer page (independent module)
const CardDesignerPage = lazy(() => import('./pages/CardDesigner'));

// Modern Homepage Component - Redesigned with Stripe/Linear aesthetic (memoized for performance)
const ResetSuccessPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ContactLogo className="h-12 w-auto mx-auto" />
          <div className="mt-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="mt-4 text-xl font-bold text-gray-900">¡Contraseña actualizada!</h2>
          <p className="mt-2 text-sm text-gray-600">Tu contraseña fue actualizada correctamente.</p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/login')}
              className="btn-primary"
            >
              Iniciar sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reset Password Page Component (old - keeping for backward compatibility)
const ResetPasswordPage = () => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      setError('Token de restablecimiento no proporcionado');
      setVerifying(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    setVerifying(true);
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${tokenToVerify}`);
      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      } else {
        setError(response.data.message || 'Token inválido');
        setTokenValid(false);
      }
    } catch (error) {
      setError('Error al verificar el enlace');
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!/[A-Z]/.test(password)) {
      return 'La contraseña debe incluir al menos una letra mayúscula';
    }
    if (!/[a-z]/.test(password)) {
      return 'La contraseña debe incluir al menos una letra minúscula';
    }
    if (!/[0-9]/.test(password)) {
      return 'La contraseña debe incluir al menos un número';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      // Password reset successful, auto-login user
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setSuccess(true);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      setError(error.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <ContactLogo className="h-12 w-auto mx-auto" />
            <div className="mt-6 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-sm text-gray-600">Verificando enlace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <ContactLogo className="h-12 w-auto mx-auto" />
            <div className="mt-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-bold text-gray-900">Enlace inválido</h2>
            <p className="mt-2 text-sm text-gray-600">El enlace no es válido o ha expirado. Solicita uno nuevo.</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="btn-primary"
              >
                Volver al inicio de sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <ContactLogo className="h-12 w-auto mx-auto" />
            <div className="mt-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-bold text-gray-900">¡Contraseña actualizada!</h2>
            <p className="mt-2 text-sm text-gray-600">Tu contraseña fue actualizada correctamente.</p>
            <p className="mt-1 text-sm text-gray-500">Redirigiendo al panel...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ContactLogo className="h-12 w-auto mx-auto" />
          <h2 className="mt-6 text-2xl font-bold text-black">
            Crear nueva contraseña
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Para: {email}
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 card-shadow-lg sm:rounded-xl sm:px-10 border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="error-state">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="new-password" className="form-label">
                Nueva contraseña
              </label>
              <div className="mt-1">
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-input"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Debe incluir: mayúsculas, minúsculas y números
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="form-label">
                Confirmar contraseña
              </label>
              <div className="mt-1">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input"
                  placeholder="Repite la nueva contraseña"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? 'Guardando nueva contraseña...' : 'Guardar nueva contraseña'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Volver al inicio de sesión
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Comprar Page Component
const Dashboard = () => {
  const { user, logout, isImpersonating, stopImpersonating, updateUser: updateAuthUser } = useAuth();
  const { openUpgrade, refreshPlan, plan } = usePlan();
  const { t } = useLanguage();
  const [showProTour, setShowProTour] = useState(false);
  useEffect(() => {
    // Triggered here (dashboard mount/plan refresh) rather than on the
    // post-checkout success page, since that page only polls for ~10
    // seconds — if MercadoPago confirms the payment after that window,
    // is_pro would never flip true while the user is still there, and
    // the tour would never show at all. Checking it here instead means
    // it reliably shows the first time a Pro user's plan is loaded,
    // however that happened.
    //
    // Gated on tutorial_completed — account-level, backend-persisted (same
    // field/endpoint RegisterOnboardingPage already uses to skip this same
    // tour) — instead of a localStorage flag, which was per-device and made
    // the tour reappear every time the same account logged in elsewhere.
    if (!plan?.is_pro || !user?.id || user?.tutorial_completed) return;
    // Marked as seen the moment it's shown, not on dismiss — on mobile the
    // tab is much more likely to get backgrounded/killed or silently
    // reloaded mid-tour (app switch, screen lock, memory pressure) than a
    // user explicitly clicking "Saltar"/"Empezar a usar Pro". Waiting
    // until dismiss meant an interrupted tour never got marked, so it
    // reappeared on every single visit until someone finished it in one go.
    updateAuthUser({ tutorial_completed: true });
    axios.put(`${API}/users/tutorial-status`, { tutorial_completed: true }).catch(() => {});
    setShowProTour(true);
  }, [plan?.is_pro, user?.id, user?.tutorial_completed]);
  const dismissProTour = () => {
    setShowProTour(false);
  };
  useHeartbeat();
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // ?tab= lets external links (e.g. the homepage account menu) deep-link
    // straight into a specific tab instead of always landing on the default.
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    if (requestedTab) return requestedTab;
    // Set initial tab based on access type
    const accessType = localStorage.getItem('access_type');
    return accessType === 'team_key' ? 'profile' : 'profiles';
  });
  const [analytics, setAnalytics] = useState(null);
  const [monthlyComparison, setMonthlyComparison] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [activationCodes, setActivationCodes] = useState([]);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [showPostRegisterPlans, setShowPostRegisterPlans] = useState(false);
  const [accessType, setAccessType] = useState(() => {
    return localStorage.getItem('access_type') || 'normal';
  });
  const [teamName, setTeamName] = useState(() => {
    return localStorage.getItem('team_name') || '';
  });
  const [restrictedProfileId, setRestrictedProfileId] = useState(() => {
    return localStorage.getItem('team_profile_id') || null;
  });
  const navigate = useNavigate();

  // Check session type on mount
  useEffect(() => {
    const checkSessionType = async () => {
      const storedAccessType = localStorage.getItem('access_type');
      if (storedAccessType === 'team_key') {
        setAccessType('team_key');
        setTeamName(localStorage.getItem('team_name') || '');
        setRestrictedProfileId(localStorage.getItem('team_profile_id'));
        setActiveTab('profile'); // Start at profile tab for team users
      }
    };
    checkSessionType();
  }, []);

  // Cmd+K / Ctrl+K opens the quick-navigation command palette.
  // "?" opens the shortcuts help modal — ignored while typing in a field so
  // it doesn't hijack a literal "?" character in a search box or textarea.
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((open) => !open);
        return;
      }
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = e.target?.tagName;
        const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable;
        if (!isTyping) {
          e.preventDefault();
          setIsShortcutsOpen((open) => !open);
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Registration-onboarding "choose your plan" popup — shown once, right
  // after account creation, inside the dashboard rather than on the
  // onboarding page itself (see RegisterOnboardingPage).
  useEffect(() => {
    if (sessionStorage.getItem('cn_show_plans_modal')) {
      sessionStorage.removeItem('cn_show_plans_modal');
      setTimeout(() => setShowPostRegisterPlans(true), 400);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
    if (activeTab === 'admin' && user.is_admin) {
      loadUsers();
      loadActivationCodes();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'analytics' && currentProfile) {
      loadAnalytics(currentProfile.id);
      loadMonthlyComparison(currentProfile.id);
    }
  }, [activeTab, currentProfile]);

  const loadProfiles = async () => {
    try {
      if (accessType === 'team_key' && restrictedProfileId) {
        // For team access, only load the restricted profile
        try {
          const response = await axios.get(`${API}/profiles/${restrictedProfileId}`);
          const profile = response.data;
          setProfiles([profile]);
          setCurrentProfile(profile);
        } catch (error) {
          // If team profile fails, clear team access data and reload as normal
          console.error('Error loading team profile, switching to normal mode:', error);
          localStorage.removeItem('access_type');
          localStorage.removeItem('team_profile_id');
          localStorage.removeItem('team_name');
          setAccessType('normal');
          setRestrictedProfileId(null);
          // Reload as normal user
          const response = await axios.get(`${API}/profiles/me`);
          const profilesList = response.data;
          setProfiles(profilesList);
          if (profilesList.length > 0) {
            setCurrentProfile(profilesList[0]);
          }
        }
      } else {
        // Normal access - load all profiles
        const response = await axios.get(`${API}/profiles/me`);
        const profilesList = response.data;
        setProfiles(profilesList);

        if (profilesList.length > 0 && !currentProfile) {
          const defaultProfile = profilesList.find((p) => p.is_default);
          setCurrentProfile(defaultProfile || profilesList[0]);
        }
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async (profileId) => {
    try {
      const response = await axios.get(`${API}/analytics/${profileId}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadMonthlyComparison = async (profileId, period = '3m') => {
    try {
      const response = await axios.get(`${API}/analytics/${profileId}/monthly-comparison?period=${period}`);
      setMonthlyComparison(response.data);
    } catch (error) {
      console.error('Error loading monthly comparison:', error);
    }
  };

  const loadUsers = async () => {
    try {
      // Load all users with pagination - fetch all pages
      let allUsers = [];
      let page = 1;
      let totalUsers = 0;
      let hasMore = true;
      
      while (hasMore) {
        const response = await axios.get(`${API}/admin/users?page=${page}&limit=200`);
        const data = response.data;
        
        if (data.users && data.users.length > 0) {
          allUsers = [...allUsers, ...data.users];
        }
        
        if (data.pagination) {
          totalUsers = data.pagination.total;
          hasMore = data.pagination.has_next;
        } else {
          hasMore = false;
        }
        page++;
        
        // Safety limit to prevent infinite loops
        if (page > 10) break;
      }
      
      setUsers(allUsers);
      setTotalUsersCount(totalUsers || allUsers.length);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadActivationCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/activation-codes`);
      setActivationCodes(response.data);
    } catch (error) {
      console.error('Error loading activation codes:', error);
    }
  };

  const createNewProfile = async () => {
    try {
      const response = await axios.post(`${API}/profiles/create`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      await loadProfiles();
      setCurrentProfile(response.data);
      setActiveTab('profile');
      return { success: true };
    } catch (error) {
      console.error('Error creating profile:', error);
      // Detect plan-upgrade response and open the upgrade modal
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402 && detail?.code === 'plan_upgrade_required') {
        openUpgrade(detail.feature || 'multiple_profiles');
        return { success: false, error: detail.message || 'Necesitas plan Pro', upgrade_required: true };
      }
      const errorMessage = typeof detail === 'string'
        ? detail
        : (detail?.message || error.message || 'Error al crear perfil');
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const updateProfile = async (updates) => {
    if (!currentProfile) return { success: false, error: 'No profile selected' };
    
    try {
      const response = await axios.put(`${API}/profile/${currentProfile.id}`, updates);
      
      // Validate response before setting
      if (response.data && response.data.id) {
        setCurrentProfile(response.data);
        await loadProfiles();
        return { success: true };
      } else {
        console.error('Invalid response from update profile:', response.data);
        return { success: false, error: t('dashboard.invalidServerResponse', 'Respuesta inválida del servidor') };
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.response?.data?.detail || 'Error updating profile' };
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const createActivationCode = async (maxProfiles = 1) => {
    try {
      const response = await axios.post(`${API}/admin/activation-codes`, { max_profiles: maxProfiles });
      loadActivationCodes();
      return response.data;
    } catch (error) {
      console.error('Error creating activation code:', error);
      return null;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#f5f5f7', display: 'flex' }}>
        <style>{SKELETON_CSS}</style>
        {/* Sidebar skeleton */}
        <div style={{ width: 224, minWidth: 224, background: '#FAFAFA', borderRight: '1px solid #E5E5E5', padding: '18px 14px', display: 'flex', flexDirection: 'column', gap: 18 }} className="hidden lg:flex">
          <Skeleton width={32} height={32} radius={8} />
          <div style={{ background: '#fff', border: '1px solid #E5E5E5', borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton width="70%" height={13} />
            <Skeleton width={60} height={16} radius={980} />
          </div>
          {[1, 2, 3].map((group) => (
            <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Skeleton width={70} height={10} />
              {[1, 2, 3].map((item) => (
                <Skeleton key={item} width="100%" height={30} radius={9} />
              ))}
            </div>
          ))}
        </div>
        {/* Content skeleton */}
        <div style={{ flex: 1, padding: 32, maxWidth: 1100 }}>
          <Skeleton width={220} height={26} style={{ marginBottom: 10 }} />
          <Skeleton width={320} height={13} style={{ marginBottom: 28 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {[1, 2, 3, 4, 5, 6].map((card) => (
              <div key={card} style={{ background: '#fff', border: '1px solid #E5E5E5', borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Skeleton width={44} height={44} radius={22} />
                <Skeleton width="80%" height={14} />
                <Skeleton width="55%" height={12} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background:'#f5f5f7'}}>
      {showPostRegisterPlans && (
        <PostRegisterPlansModal onClose={() => setShowPostRegisterPlans(false)} />
      )}

      {/* Command Palette (Cmd+K / Ctrl+K quick navigation) */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        items={getMenuItems({ accessType, user, plan, t })}
        onSelect={(tabId) => {
          setActiveTab(tabId);
          setIsCommandPaletteOpen(false);
        }}
      />

      {/* Keyboard Shortcuts help modal (? key) */}
      <ShortcutsModal
        isOpen={isShortcutsOpen}
        onClose={() => setIsShortcutsOpen(false)}
      />

      {/* Pro onboarding tour — see ProOnboardingTour.js's docstring for
          why this lives here instead of on the post-checkout success
          page. "Ver ahora" switches activeTab directly and minimizes
          itself instead of ending the tour, so exploring a section
          doesn't cost the rest of the walkthrough. */}
      {showProTour && (
        <ProOnboardingTour
          onFinish={dismissProTour}
          onViewTab={(tabId) => setActiveTab(tabId)}
        />
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Admin impersonation banner — always visible while "viendo como" a user */}
      {isImpersonating && (
        <div
          data-testid="impersonation-banner"
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-3 text-sm font-medium text-white"
          style={{ background: '#B45309', padding: '8px 16px' }}
        >
          <span>Estás viendo como {user?.email}</span>
          <button
            onClick={() => { stopImpersonating(); window.location.href = '/admin'; }}
            className="px-3 py-1 rounded-md font-semibold transition hover:opacity-90"
            style={{ background: 'rgba(255,255,255,0.2)' }}
          >
            Volver a admin
          </button>
        </div>
      )}

      {/* Responsive Layout Container */}
      <div className="flex min-h-screen" style={{background:'transparent', paddingTop: isImpersonating ? 38 : 0}}>
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onLogout={handleLogout}
          user={user}
          profiles={profiles}
          currentProfile={currentProfile}
          setCurrentProfile={setCurrentProfile}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
          onOpenShortcuts={() => setIsShortcutsOpen(true)}
          accessType={accessType}
          teamName={teamName}
        />

        {/* Main Content Area - Maximum Horizontal Space */}
        <div className="flex-1 lg:ml-0">
          {/* Mobile Header with Hamburger Menu */}
          <div className="lg:hidden dashboard-header flex items-center justify-between">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <ContactLogo className="h-6 w-auto" clickable={true} />
            <div className="w-6" /> {/* Spacer for centering logo */}
          </div>

          {/* Main Content */}
          <div key={activeTab} className="p-6 min-h-screen dashboard-tab-content" style={{background:'transparent'}}>
            {activeTab === 'profiles' && (
              <ProfilesManager
                profiles={profiles}
                currentProfile={currentProfile}
                setCurrentProfile={setCurrentProfile}
                createNewProfile={createNewProfile}
                refreshProfiles={loadProfiles}
                user={user}
                setActiveTab={setActiveTab}
              />
            )}
            {activeTab === 'profile' && currentProfile && (
              <ProfileEditor 
                profile={currentProfile} 
                updateProfile={updateProfile}
                refreshProfile={async (profileId) => {
                  try {
                    const response = await axios.get(`${API}/profiles/${profileId}`);
                    setCurrentProfile(response.data);
                    await loadProfiles(); // Also refresh the profiles list
                  } catch (error) {
                    console.error('Error refreshing profile:', error);
                  }
                }}
              />
            )}

            {activeTab === 'leads' && currentProfile && (
              // Leads is partially free: LeadsPanel itself renders the
              // monthly-quota locked rows inline, so it isn't wrapped in
              // <LockedFeature> (that would block the whole section).
              <LeadsPanel profile={currentProfile} user={user} />
            )}

            {activeTab === 'campaigns' && (
              <LockedFeature feature="campaigns">
                <ComingSoonOverlay section="campaigns">
                  <CampaignsPanel profiles={profiles} />
                </ComingSoonOverlay>
              </LockedFeature>
            )}

            {activeTab === 'teams' && (
              <LockedFeature feature="teams">
                <TeamsPanel user={user} profiles={profiles} />
              </LockedFeature>
            )}

            {activeTab === 'team_analytics' && (
              <LockedFeature feature="team_analytics">
                <TeamAnalyticsPanel />
              </LockedFeature>
            )}

            {activeTab === 'analytics' && currentProfile && (
              // Analytics is partially free: the 4 headline KPI cards are
              // always visible, AnalyticsPanel wraps only the rest (charts,
              // breakdowns, insights) in <LockedFeature> internally.
              <AnalyticsPanel analytics={analytics} profile={currentProfile} monthlyComparison={monthlyComparison} user={user} />
            )}

            {activeTab === 'integrations' && (
              // IntegrationsPanel handles its own auth/plan-aware gating
              // internally (loading skeleton -> locked upgrade view ->
              // operative panel) with a bespoke locked view designed for
              // this feature specifically — not wrapped in the generic
              // <LockedFeature>, whose "render children while loading"
              // behavior is fine for other tabs but would fight with the
              // stricter "never flash enabled controls" requirement here.
              <ComingSoonOverlay section="integrations">
                <IntegrationsPanel setActiveTab={setActiveTab} />
              </ComingSoonOverlay>
            )}

            {activeTab === 'appearance' && currentProfile && (
              <AppearanceEditor profile={currentProfile} updateProfile={updateProfile} setActiveTab={setActiveTab} />
            )}
            
            {activeTab === 'hub' && (
              <LockedFeature feature="hub">
                <HubPanel user={user} />
              </LockedFeature>
            )}

            {activeTab === 'followups' && (
              <LockedFeature feature="reminders">
                <ComingSoonOverlay section="followups">
                  <FollowUpsPanel user={user} />
                </ComingSoonOverlay>
              </LockedFeature>
            )}

            {activeTab === 'developer' && (
              // DeveloperPanel handles its own auth/plan-aware gating
              // internally, same reasoning as IntegrationsPanel above — it
              // can reveal real API keys/webhook secrets, so it must never
              // flash enabled controls via <LockedFeature>'s
              // render-children-while-loading behavior.
              <ComingSoonOverlay section="developer">
                <DeveloperPanel />
              </ComingSoonOverlay>
            )}
            
            {activeTab === 'settings' && (
              <SettingsPanel
                user={user}
                onUserUpdate={(updatedUser) => {
                  setUser(updatedUser);
                }}
                setActiveTab={setActiveTab}
              />
            )}
            
            {activeTab === 'admin' && user?.is_admin && (
              <AdminPanelInline 
                users={users} 
                activationCodes={activationCodes}
                updateUser={updateUser}
                createCode={createActivationCode}
                totalUsersCount={totalUsersCount}
              />
            )}
            {activeTab === 'product' && user?.is_admin && (
              <ProductManagementPanel />
            )}
            {activeTab === 'orders' && user?.is_admin && (
              <AdminOrdersTab />
            )}
            {activeTab === 'subscriptions' && user?.is_admin && (
              <AdminSubscriptionsTab />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Profiles Manager Component
const InsightsModule = ({ profile, dateRange }) => {
  const [insights, setInsights] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (profile) {
      loadInsights();
    }
  }, [profile, dateRange]);

  const loadInsights = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/${profile.id}/insights`, {
        params: { date_range: dateRange },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setInsights(response.data.insights || []);
      setPredictions(response.data.predictions || []);
      setLastUpdated(response.data.generated_at);
      
    } catch (error) {
      console.error('Error loading insights:', error);
      setInsights([]);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getIconName = (iconName) => {
    const iconMap = {
      'trending-up': 'trending',
      'star': 'star',
      'target': 'star',
      'network': 'globe',
      'rocket': 'trending',
      'alert': 'warning',
      'calendar': 'calendar',
      'play': 'play'
    };
    return iconMap[iconName] || 'brain';
  };

  return (
    <div className="bg-white card-shadow-lg rounded-xl p-4 lg:p-6 h-full">
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
          <Icon name="brain" className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
        </div>
        <h3 className="text-base lg:text-lg font-semibold text-black">Insights IA</h3>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-300 mx-auto"></div>
          <p className="mt-2 text-gray-600 text-sm">Analizando datos...</p>
        </div>
      ) : insights.length > 0 ? (
        <div className="space-y-3">
          {/* Last Updated Indicator */}
          {lastUpdated && (
            <div className="text-xs text-gray-500 text-center mb-3 pb-2 border-b border-gray-100">
              Actualizado: {new Date(lastUpdated).toLocaleString('es-ES', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
              })}
            </div>
          )}

          {/* Insights */}
          {insights.slice(0, 3).map((insight, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center">
                  <Icon 
                    name={getIconName(insight.icon)} 
                    className="w-4 h-4 text-purple-600 mr-2 flex-shrink-0 mt-0.5" 
                  />
                  <h4 className="font-semibold text-sm text-black">{insight.title}</h4>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(insight.priority)}`}>
                  {insight.priority === 'high' ? 'Alta' : insight.priority === 'medium' ? 'Media' : 'Baja'}
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-2 leading-relaxed">
                {insight.description}
              </p>
              <div className="bg-gray-50 rounded-md p-2">
                <p className="text-xs font-medium text-gray-800">
                  💡 {insight.action}
                </p>
              </div>
            </div>
          ))}

          {/* Predictions Section */}
          {predictions.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-black mb-3 flex items-center">
                <Icon name="trending" className="w-4 h-4 text-blue-600 mr-2" />
                Predicciones IA
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {predictions.map((prediction, index) => (
                  <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <h5 className="text-xs font-medium text-blue-900">{prediction.title}</h5>
                      <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                        {prediction.confidence}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-blue-800">
                        {typeof prediction.value === 'number' ? prediction.value : prediction.value}
                      </span>
                      <div className="flex items-center text-xs">
                        {prediction.trend === 'up' && <span className="text-green-600">📈</span>}
                        {prediction.trend === 'down' && <span className="text-red-600">📉</span>}
                        {prediction.trend === 'stable' && <span className="text-gray-600">➡️</span>}
                        {prediction.trend === 'positive' && <span className="text-green-600">✅</span>}
                        {prediction.trend === 'negative' && <span className="text-red-600">⚠️</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="brain" className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 font-medium text-sm">No hay insights disponibles</p>
          <p className="text-xs text-gray-400 mt-1">
            Necesitas más actividad para generar recomendaciones
          </p>
        </div>
      )}
    </div>
  );
};

// ── Heartbeat hook — pings /api/heartbeat every 30s for any logged-in user ──
const useHeartbeat = () => {
  useEffect(() => {
    const ping = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      axios.post(`${API}/heartbeat`).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 30000);
    return () => clearInterval(id);
  }, []);
};

// ── Live users widget (admin only) ──────────────────────────────────────────
const LiveUsersWidget = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const BLUE = '#0071e3';

  const relTime = iso => {
    if (!iso) return '';
    const s = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (s < 60) return 'ahora';
    if (s < 3600) return `hace ${Math.floor(s/60)} min`;
    return `hace ${Math.floor(s/3600)} h`;
  };

  const load = async () => {
    try { const r = await axios.get(`${API}/admin/active-users`); setData(r.data); } catch {}
  };

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  if (!data) return null;

  const metrics = [
    { label: '2 min',    value: data.live_2min  },
    { label: '5 min',    value: data.live_5min  },
    { label: '15 min',   value: data.live_15min },
    { label: '1 hora',   value: data.live_60min },
  ];
  const users = expanded ? data.recent_users : data.recent_users.slice(0, 4);

  return (
    <div style={{background:'#fff', border:'1px solid rgba(0,0,0,.07)', borderRadius:18, marginBottom:28, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,.04)'}}>
      {/* Header row */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderBottom: data.recent_users.length ? '1px solid rgba(0,0,0,.06)' : 'none', gap:16, flexWrap:'wrap'}}>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <div style={{position:'relative', width:7, height:7, flexShrink:0}}>
            <span style={{position:'absolute',inset:0,borderRadius:'50%',background:BLUE,opacity:.3,display:'block',animation:'lwRing 2s ease-out infinite'}}/>
            <span style={{position:'absolute',inset:0,borderRadius:'50%',background:BLUE,display:'block'}}/>
          </div>
          <div>
            <span style={{fontSize:12, fontWeight:600, color:'#1d1d1f', letterSpacing:'-.01em'}}>Usuarios activos ahora</span>
            <span style={{fontSize:11, color:'#a1a1a6', marginLeft:6}}>Usuarios con sesión abierta en la plataforma · actualiza cada 30s</span>
          </div>
        </div>
        <div style={{display:'flex', gap:0}}>
          {metrics.map(({ label, value }, i) => (
            <div key={label} style={{textAlign:'center', padding:'0 16px', borderLeft: i > 0 ? '1px solid rgba(0,0,0,.07)' : 'none'}}>
              <div style={{fontSize:20, fontWeight:300, color: BLUE, letterSpacing:'-.04em', lineHeight:1}}>{value}</div>
              <div style={{fontSize:10, color:'#a1a1a6', marginTop:2}}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* User list */}
      {data.recent_users.length > 0 && (
        <div style={{padding:'2px 0 4px'}}>
          {users.map((u, i) => (
            <div key={u.id} style={{display:'flex', alignItems:'center', gap:10, padding:'7px 20px', background: i%2===0 ? 'transparent' : 'rgba(0,0,0,.012)'}}>
              <div style={{width:24, height:24, borderRadius:'50%', background:'rgba(0,113,227,.07)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                <span style={{fontSize:10, fontWeight:600, color:BLUE}}>{(u.name||u.email||'?')[0].toUpperCase()}</span>
              </div>
              <span style={{fontSize:12, color:'#1d1d1f', fontWeight:500, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{u.name || u.email}</span>
              <span style={{fontSize:11, color:'#a1a1a6', flexShrink:0}}>{u.email}</span>
              <span style={{fontSize:9, fontWeight:600, padding:'2px 7px', borderRadius:100, flexShrink:0,
                background: u.plan==='pro' ? 'rgba(0,113,227,.08)' : 'rgba(0,0,0,.04)',
                color: u.plan==='pro' ? BLUE : '#a1a1a6'
              }}>{u.plan==='pro'?'Pro':'Free'}</span>
              <span style={{fontSize:11, color:'#a1a1a6', flexShrink:0, minWidth:48, textAlign:'right'}}>{relTime(u.last_seen)}</span>
            </div>
          ))}
          {data.recent_users.length > 4 && (
            <button onClick={() => setExpanded(v => !v)} style={{display:'block', width:'100%', padding:'6px 0', background:'none', border:'none', borderTop:'1px solid rgba(0,0,0,.05)', fontSize:11, color:BLUE, cursor:'pointer', fontWeight:500, marginTop:2}}>
              {expanded ? 'Ver menos' : `Ver ${data.recent_users.length - 4} más`}
            </button>
          )}
        </div>
      )}
      {data.recent_users.length === 0 && (
        <div style={{padding:'10px 20px', fontSize:12, color:'#a1a1a6'}}>Sin actividad en los últimos 5 minutos.</div>
      )}
      <style>{`@keyframes lwRing{0%{transform:scale(1);opacity:.35}100%{transform:scale(2.8);opacity:0}}`}</style>
    </div>
  );
};

// Admin Panel Inline Component (for dashboard tab)
const ADMIN_FONT = "'abcNormal','Inter','Arial',sans-serif";
const ADMIN_FONT_FACE = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;
const AdminPanelInline = ({ users, activationCodes, updateUser, createCode, totalUsersCount }) => {
  const { confirm } = useNotification();
  const [activeAdminTab, setActiveAdminTab] = useState('users');
  const [quotations, setQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [profilesPagination, setProfilesPagination] = useState({
    page: 1, limit: 50, total: 0, total_pages: 0, has_next: false, has_prev: false
  });
  const [profileSearchTerm, setProfileSearchTerm] = useState('');
  const [profileStatusFilter, setProfileStatusFilter] = useState('all');
  
  // Estados para el panel de recordatorios
  const [reminders, setReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [reminderFilters, setReminderFilters] = useState({
    status: '',
    profile: ''
  });
  const [reminderPagination, setReminderPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });

  // Estados para el panel de waitlist
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);
  const [waitlistPagination, setWaitlistPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });

  useEffect(() => {
    console.log('Admin tab changed to:', activeAdminTab);
    if (activeAdminTab === 'quotations') {
      loadQuotations();
    } else if (activeAdminTab === 'profiles' && !profilesLoaded) {
      console.log('Loading profiles triggered by tab change');
      loadProfiles();
    } else if (activeAdminTab === 'reminders') {
      loadReminders();
    } else if (activeAdminTab === 'waitlist') {
      loadWaitlistEntries();
    }
  }, [activeAdminTab, profilesLoaded]);

  // Also load profiles when component mounts if profiles tab is active
  useEffect(() => {
    if (activeAdminTab === 'profiles' && profiles.length === 0 && !loadingProfiles && !profilesLoaded) {
      console.log('Loading profiles on component mount for active profiles tab');
      loadProfiles();
    }
  }, [activeAdminTab, profiles.length, loadingProfiles, profilesLoaded]);

  const loadQuotations = async () => {
    setLoadingQuotations(true);
    try {
      const response = await axios.get(`${API}/admin/quotations`);
      // Backend now returns { quotations: [...], pagination: {...} }
      setQuotations(response.data.quotations || response.data);
    } catch (error) {
      console.error('Error loading quotations:', error);
    } finally {
      setLoadingQuotations(false);
    }
  };

  const loadProfiles = async (page = null, search = null, status = null) => {
    setLoadingProfiles(true);
    const targetPage = page || profilesPagination.page;
    const targetSearch = search !== null ? search : profileSearchTerm;
    const targetStatus = status !== null ? status : profileStatusFilter;
    try {
      const params = new URLSearchParams({ page: targetPage, limit: profilesPagination.limit });
      if (targetSearch && targetSearch.trim()) params.append('search', targetSearch.trim());
      if (targetStatus && targetStatus !== 'all') params.append('status', targetStatus);
      const response = await axios.get(`${API}/admin/profiles?${params}`);
      const data = response.data;
      const profilesData = Array.isArray(data) ? data : data.profiles || [];
      setProfiles(profilesData);
      if (data.pagination) {
        setProfilesPagination(prev => ({ ...prev, ...data.pagination }));
      }
      setProfilesLoaded(true);
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const loadReminders = async () => {
    setLoadingReminders(true);
    try {
      const params = new URLSearchParams({
        skip: reminderPagination.skip.toString(),
        limit: reminderPagination.limit.toString(),
      });
      
      if (reminderFilters.status) {
        params.append('status_filter', reminderFilters.status);
      }
      if (reminderFilters.profile) {
        params.append('profile_filter', reminderFilters.profile);
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/reminders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReminders(response.data.reminders);
      setReminderPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      console.error('Error loading reminders:', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const loadWaitlistEntries = async () => {
    setLoadingWaitlist(true);
    try {
      const params = new URLSearchParams({
        skip: waitlistPagination.skip.toString(),
        limit: waitlistPagination.limit.toString(),
      });

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/waitlist?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setWaitlistEntries(response.data.entries);
      setWaitlistPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      console.error('Error loading waitlist entries:', error);
    } finally {
      setLoadingWaitlist(false);
    }
  };

  const updateQuotationStatus = async (quotationId, status) => {
    try {
      await axios.put(`${API}/admin/quotations/${quotationId}`, { status });
      await loadQuotations(); // Reload quotations
    } catch (error) {
      console.error('Error updating quotation status:', error);
    }
  };

  const deleteProfile = async (profileId) => {
    if (await confirm('¿Estás seguro de que quieres eliminar este perfil? Esta acción no se puede deshacer.', { confirmLabel: 'Eliminar' })) {
      try {
        await axios.delete(`${API}/admin/profiles/${profileId}`);
        setProfilesLoaded(false); // Force reload
        await loadProfiles(); // Reload profiles
      } catch (error) {
        console.error('Error deleting profile:', error);
      }
    }
  };

  const toggleProfileStatus = async (profileId, isActive) => {
    try {
      await axios.put(`${API}/admin/profiles/${profileId}/status`, { is_active: !isActive });
      setProfilesLoaded(false); // Force reload
      await loadProfiles(); // Reload profiles
    } catch (error) {
      console.error('Error updating profile status:', error);
    }
  };

  return (
    <div className="w-full">
      <style>{ADMIN_FONT_FACE}</style>
      <div className="mb-8">
        <h1 style={{ fontSize: 28, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', fontFamily: ADMIN_FONT }}>Panel de Administración</h1>
        <p className="text-gray-600 mt-2">Gestiona usuarios y códigos de activación</p>
      </div>

      {/* Admin Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex flex-wrap gap-x-6 gap-y-1">
          <button
            onClick={() => setActiveAdminTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'users'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Usuarios ({totalUsersCount})
          </button>
          <button
            onClick={() => setActiveAdminTab('companies')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'companies'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Empresas
          </button>
          <button
            onClick={() => setActiveAdminTab('codes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'codes'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Códigos de Activación ({activationCodes.length})
          </button>
          <button
            onClick={() => setActiveAdminTab('quotations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'quotations'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cotizaciones ({quotations.length})
          </button>
          <button
            onClick={() => setActiveAdminTab('profiles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'profiles'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Perfiles ({profilesPagination.total || profiles.length})
          </button>
          <button
            onClick={() => setActiveAdminTab('reminders')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'reminders'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Recordatorios ({reminders.length})
          </button>
          <button
            onClick={() => setActiveAdminTab('waitlist')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'waitlist'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Waitlist ({waitlistPagination.total || waitlistEntries.length})
          </button>
          <button
            onClick={() => setActiveAdminTab('analytics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'analytics'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 Analíticas Globales
          </button>
          <button
            onClick={() => setActiveAdminTab('quotation_config')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'quotation_config'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ⚙️ Config. Cotizaciones
          </button>
          <button
            onClick={() => setActiveAdminTab('beneficios')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'beneficios'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Beneficios
          </button>
          <button
            onClick={() => setActiveAdminTab('landing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'landing'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="admin-tab-landing"
          >
            Landing Page
          </button>
          <button
            onClick={() => setActiveAdminTab('platform_settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'platform_settings'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="admin-tab-platform-settings"
          >
            Configuración
          </button>
          <button
            onClick={() => setActiveAdminTab('plans')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'plans'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="admin-tab-plans"
          >
            Planes
          </button>
          <button
            onClick={() => setActiveAdminTab('client_intelligence')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeAdminTab === 'client_intelligence'
                ? 'border-sky-300 text-sky-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="admin-tab-client-intelligence"
          >
            Inteligencia de Clientes
          </button>
        </nav>
      </div>

      {activeAdminTab === 'users' && (
        <AdminUsersTab users={users} updateUser={updateUser} />
      )}
      {activeAdminTab === 'companies' && (
        <AdminCompaniesTab users={users} />
      )}
      {activeAdminTab === 'codes' && (
        <AdminCodesTab codes={activationCodes} createCode={createCode} />
      )}
      {activeAdminTab === 'quotations' && (
        <AdminQuotationsTab 
          quotations={quotations} 
          loading={loadingQuotations}
          updateStatus={updateQuotationStatus}
        />
      )}
      {activeAdminTab === 'profiles' && (
        <AdminProfilesTab
          profiles={profiles}
          loading={loadingProfiles}
          deleteProfile={deleteProfile}
          toggleStatus={toggleProfileStatus}
          pagination={profilesPagination}
          searchTerm={profileSearchTerm}
          filterBy={profileStatusFilter}
          onSearchChange={(value) => {
            setProfileSearchTerm(value);
            setProfilesLoaded(false);
            loadProfiles(1, value, profileStatusFilter);
          }}
          onFilterChange={(value) => {
            setProfileStatusFilter(value);
            setProfilesLoaded(false);
            loadProfiles(1, profileSearchTerm, value);
          }}
          onPageChange={(newPage) => {
            setProfilesLoaded(false);
            loadProfiles(newPage);
          }}
          reloadProfiles={() => {
            setProfilesLoaded(false);
            loadProfiles(1);
          }}
        />
      )}
      {activeAdminTab === 'reminders' && (
        <AdminRemindersTab 
          reminders={reminders} 
          loading={loadingReminders}
          filters={reminderFilters}
          setFilters={setReminderFilters}
          pagination={reminderPagination}
          setPagination={setReminderPagination}
          reloadReminders={loadReminders}
        />
      )}
      {activeAdminTab === 'waitlist' && (
        <AdminWaitlistTab
          entries={waitlistEntries}
          loading={loadingWaitlist}
          pagination={waitlistPagination}
          setPagination={setWaitlistPagination}
          reloadEntries={loadWaitlistEntries}
        />
      )}
      {activeAdminTab === 'analytics' && (
        <GlobalAnalyticsTab />
      )}
      {activeAdminTab === 'quotation_config' && (
        <QuotationConfigTab />
      )}
      {activeAdminTab === 'beneficios' && (
        <BeneficiosPanel />
      )}
      {activeAdminTab === 'landing' && (
        <LandingEditor />
      )}
      {activeAdminTab === 'platform_settings' && (
        <div className="space-y-6" style={{ maxWidth: 420 }}>
          <AdminRegistrationToggle />
          <AdminDisabledSectionsToggle />
        </div>
      )}
      {activeAdminTab === 'plans' && (
        <AdminPlansTab />
      )}
      {activeAdminTab === 'client_intelligence' && (
        <ClientIntelligenceTab />
      )}
    </div>
  );
};

// Admin Users Tab
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-300"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // CRITICAL: Allow access to /activate page if user needs activation
  // Don't create a redirect loop
  if (user.activation_used === false && location.pathname !== '/activate') {
    return <Navigate to="/activate" />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-300"></div>
      </div>
    );
  }
  
  return user && user.is_admin ? children : <Navigate to="/login" />;
};

// OAuth Callback Component
// Google OAuth Callback Component
// OAuth Callback Component
const OAuthCallback = () => {
  const { socialLogin } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get session_id from URL fragment
        const hash = window.location.hash;
        if (hash && hash.includes('session_id=')) {
          const sessionId = hash.split('session_id=')[1].split('&')[0];
          
          if (sessionId) {
            const result = await socialLogin(sessionId);
            
            if (result.success) {
              // Clear the hash from URL
              window.location.hash = '';
              
              // CRITICAL: Check if activation is required
              if (result.needs_activation) {
                // Store activation message for display
                localStorage.setItem('google_activation_message', result.activation_message || 'Debes activar tu cuenta con el código de activación antes de poder usar la plataforma.');
                navigate('/activate');
              } else {
                // Navigate to appropriate page only if activated
                if (result.is_admin) {
                  navigate('/admin');
                } else {
                  navigate('/dashboard');
                }
              }
            } else {
              setError(result.error);
              setProcessing(false);
            }
          } else {
            setError('No session ID found in URL');
            setProcessing(false);
          }
        } else {
          setError('Invalid callback URL');
          setProcessing(false);
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError('Error processing authentication');
        setProcessing(false);
      }
    };

    handleOAuthCallback();
  }, [socialLogin, navigate]);

  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <ContactLogo className="h-12 w-auto mx-auto" />
            <h2 className="mt-6 text-2xl font-bold text-black">
              Procesando inicio de sesión...
            </h2>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ContactLogo className="h-12 w-auto mx-auto" />
          <h2 className="mt-6 text-2xl font-bold text-black">
            Error de autenticación
          </h2>
          <p className="mt-4 text-sm text-red-600">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 btn-primary"
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
};

// Fires a Meta Pixel PageView on every client-side route change after the
// first one — the base snippet in public/index.html already counts the
// very first load, before this ever mounts, so skipping that first run here
// is what keeps each real page view counted exactly once in an SPA.
const MetaPixelRouteTracker = () => {
  const location = useLocation();
  const isFirstRender = useRef(true);
  const mountTimeRef = useRef(Date.now());
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // A pathname change landing within ~1.5s of mount is almost always an
    // auth-gate bounce settling, not a real user-driven navigation —
    // ProtectedRoute's <Navigate to="/login" /> for an unauthenticated
    // /dashboard visit, or LoginPage redirecting an already-authenticated
    // visit straight to /dashboard, both change the URL again within a
    // render cycle of mount. The base snippet already fired one PageView
    // for whatever URL the browser started this load at, so also tracking
    // the settled destination produced two PageViews 1-2s apart for what
    // was really one visit. A genuine click-driven navigation essentially
    // never lands this fast after the page finishes mounting, so this
    // window doesn't suppress real SPA traffic — only the redirect this
    // is aimed at.
    if (Date.now() - mountTimeRef.current < 1500) {
      return;
    }
    trackPageView();
  }, [location.pathname]);
  return null;
};

// Main App Component
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LanguageProvider>
        <NotificationProvider>
        <PlanProvider>
        <BrowserRouter>
          <MetaPixelRouteTracker />
          <Routes>
            <Route path="/" element={<Homepage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/team-login" element={<TeamLoginPage />} />
          <Route path="/register" element={<RegisterOnboardingPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Public Password Reset Routes */}
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset" element={<PublicResetPasswordPage />} />
          <Route path="/auth/reset/success" element={<ResetSuccessPage />} />
          <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
          <Route path="/profile" element={<OAuthCallback />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/comprar" element={<ComprarPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:category" element={<DocsPage />} />
          <Route path="/terminos" element={<LegalPage docKey="terms" />} />
          <Route path="/privacidad" element={<LegalPage docKey="privacy" />} />
          <Route path="/tratamiento-de-datos" element={<LegalPage docKey="dataProcessing" />} />
          <Route path="/cotizar" element={<CotizarPage />} />
          <Route path="/agenda-demo" element={<AgendaDemo />} />
          <Route path="/app" element={<AppPage />} />
          <Route path="/sobre-nosotros" element={<SobreNosotros />} />
          <Route path="/app/waitlist" element={<WaitlistPage />} />
          <Route path="/pago-exitoso" element={<PaymentSuccessPage />} />
          <Route path="/suscripcion-exitosa" element={<SubscriptionSuccessPage />} />
          <Route path="/pago-fallido" element={<PaymentFailurePage />} />
          <Route path="/pago-pendiente" element={<PaymentPendingPage />} />
          <Route path="/activate" element={
            <ProtectedRoute>
              <ActivationPage />
            </ProtectedRoute>
          } />
          <Route path="/planes" element={
            <ProtectedRoute>
              <PlansPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <Dashboard />
            </AdminRoute>
          } />
          <Route path="/pages/:slug" element={
            <Suspense fallback={<PageLoader />}>
              <PublicProfile />
            </Suspense>
          } />
          {/* Instant-loading public profiles (Static HTML + Progressive Enhancement) */}
          <Route path="/p/:slug" element={
            <Suspense fallback={<PageLoader />}>
              <PublicProfile />
            </Suspense>
          } />
          {/* Card Designer - Independent route accessible to any user */}
          <Route path="/disenotarj" element={
            <Suspense fallback={<PageLoader />}>
              <CardDesignerPage />
            </Suspense>
          } />
          {/* Catch-all — previously missing entirely, so any unmatched URL
              (typo, dead link) rendered a blank white screen. Must be the
              last <Route>, matched only when nothing above did. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <ChatBot />
        <UpgradeModal />
        <ProfileSelectionModal />
      </BrowserRouter>
      </PlanProvider>
      </NotificationProvider>
      </LanguageProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
